/**
 * Kanban — Página principal (0258 Fase D)
 * Usa useKanbanData, useKanbanSocket, KanbanBanner, KanbanIndicator.
 */
import React, { useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import KanbanBoard from "../../components/KanbanBoard";
import { KanbanBanner, KanbanIndicator } from "../../components/Kanban";
import useKanbanData from "./hooks/useKanbanData";
import useKanbanSocket from "./hooks/useKanbanSocket";
import logger from "../../utils/logger";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  boardWrapper: {
    flex: 1,
    minHeight: 0,
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const {
    tags,
    setTags,
    tickets,
    setTickets,
    agendaActive,
    profileName,
    isInitialized,
    refetch,
  } = useKanbanData();

  useKanbanSocket({ isInitialized, user, setTags, setTickets, refetch });

  // 0266: Mapeo columna Kanban → status de cita (para cards appointment-X)
  const tagNameToStatus = (tagName) => {
    if (!tagName) return null;
    const n = String(tagName).toLowerCase();
    if (n.includes("completada") || n.includes("completa") || n.includes("completed") || n.includes("concluíd")) return "completed";
    if (n.includes("confirmada") || n.includes("confirmed")) return "confirmed";
    if (n.includes("curso") || n.includes("en progreso") || n.includes("progress")) return "in_progress";
    if (n.includes("interesado") || n.includes("interessad") || n.includes("pending")) return "pending";
    if (n.includes("abandon") || n.includes("paus") || n.includes("cancel")) return "cancelled";
    return null;
  };

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      let targetTag = null;
      if (targetLaneId.startsWith("tag-")) {
        const targetTagId = parseInt(targetLaneId.replace("tag-", ""), 10);
        targetTag = tags.find((t) => t.id === targetTagId);
      }

      if (!targetTag && targetLaneId !== "abiertos") {
        toast.error(i18n.t("kanban.tagNotFound"));
        return;
      }

      // 0266: Si es card de cita (appointment-X), actualizar status de la cita
      if (typeof cardId === "string" && cardId.startsWith("appointment-")) {
        if (!targetTag) {
          toast.error(i18n.t("kanban.cannotMapStatus"));
          return;
        }
        const appointmentId = parseInt(cardId.replace("appointment-", ""), 10);
        const newStatus = tagNameToStatus(targetTag.name);
        if (!newStatus) {
          toast.error(i18n.t("kanban.cannotMapStatus"));
          return;
        }
        await api.patch(`/appointments/${appointmentId}/status`, { status: newStatus });
        toast.success(i18n.t("kanban.appointmentUpdated"));
        refetch();
        return;
      }

      // Card de ticket (sin cita): actualizar tags del ticket
      const ticketId = typeof cardId === "string" && cardId.startsWith("ticket-")
        ? parseInt(cardId.replace("ticket-", ""), 10)
        : parseInt(cardId, 10);

      if (targetLaneId === "abiertos") {
        await api.delete(`/ticket-tags/${ticketId}`);
        toast.success(i18n.t("kanban.ticketMoved"));
        refetch();
        return;
      }

      if (sourceLaneId && sourceLaneId !== targetLaneId) {
        try {
          await api.delete(`/ticket-tags/${ticketId}`);
        } catch (_) {}
      }

      await api.put(`/ticket-tags/${ticketId}/${targetTag.id}`);
      toast.success(i18n.t("kanban.ticketMoved"));
      refetch();
    } catch (err) {
      logger.dashboard.error("Error moviendo card:", err);
      toast.error(i18n.t("kanban.moveError"));
    }
  };

  const handleCardClick = (ticket) => {
    const ticketKey = ticket?.uuid || ticket?.id;
    if (!ticketKey) return;
    history.push(`/tickets/${ticketKey}`);
  };

  return (
    <div className={classes.root}>
      {isInitialized && !agendaActive && <KanbanBanner />}
      {isInitialized && agendaActive && <KanbanIndicator profileName={profileName} />}
      <div className={classes.boardWrapper}>
        <KanbanBoard
          tickets={tickets}
          tags={tags}
          agendaActive={agendaActive}
          onCardMove={handleCardMove}
          onCardClick={handleCardClick}
        />
      </div>
    </div>
  );
};

export default Kanban;
